import { useEffect, useRef, useState } from "react";

import {
  collectMediaCardRects,
  reorderMediaIds,
  resolveMediaDropTargetId,
  resolveMediaIdFromPoint,
} from "./mediaReorder";

function pointInRect(clientX, clientY, rect) {
  if (!rect) return false;
  return (
    clientX >= rect.left
    && clientX <= rect.right
    && clientY >= rect.top
    && clientY <= rect.bottom
  );
}

function positiveAreaRect(rect) {
  if (!rect) return null;
  const width = Math.max(0, rect.right - rect.left);
  const height = Math.max(0, rect.bottom - rect.top);
  if (width <= 0 || height <= 0) return null;
  return {
    id: rect.id,
    left: rect.left,
    right: rect.right,
    top: rect.top,
    bottom: rect.bottom,
  };
}

/**
 * Dependency-free pointer reorder. Callers own ids + authorization.
 */
export function usePointerReorder({ rootRef, getIds, onReorder, enabled = true }) {
  const dragIdRef = useRef(null);
  const lastHitRectRef = useRef(null);
  const lastHitIdRef = useRef(null);
  const lastNativeEventRef = useRef(null);
  const dragListenersRef = useRef(null);
  const getIdsRef = useRef(getIds);
  const onReorderRef = useRef(onReorder);
  const [draggingId, setDraggingId] = useState(null);

  getIdsRef.current = getIds;
  onReorderRef.current = onReorder;

  const detachDragListeners = () => {
    const listeners = dragListenersRef.current;
    if (!listeners) return;
    document.removeEventListener("pointermove", listeners.onMove, true);
    document.removeEventListener("pointerup", listeners.onUp, true);
    document.removeEventListener("pointercancel", listeners.onUp, true);
    dragListenersRef.current = null;
  };

  const endDrag = () => {
    detachDragListeners();
    dragIdRef.current = null;
    lastHitRectRef.current = null;
    lastHitIdRef.current = null;
    lastNativeEventRef.current = null;
    setDraggingId(null);
  };

  useEffect(() => () => endDrag(), []);

  const reorderTowardPoint = (clientX, clientY) => {
    const fromId = dragIdRef.current;
    if (fromId == null) return;
    const current = getIdsRef.current() || [];
    const rects = collectMediaCardRects(rootRef.current, current);
    let toId = resolveMediaDropTargetId({
      clientX,
      clientY,
      fromId,
      rects,
    });
    if (toId == null) {
      toId = resolveMediaIdFromPoint(clientX, clientY, fromId);
    }
    if (toId == null || toId === fromId) {
      lastHitRectRef.current = null;
      lastHitIdRef.current = null;
      return;
    }
    const hitRect = positiveAreaRect(
      (Array.isArray(rects) ? rects : []).find((item) => item && item.id === toId),
    );
    if (hitRect && lastHitRectRef.current && lastHitRectRef.current.id === toId
      && pointInRect(clientX, clientY, lastHitRectRef.current)) {
      return;
    }
    if (!hitRect && toId === lastHitIdRef.current) return;
    const fromIndex = current.indexOf(fromId);
    const toIndex = current.indexOf(toId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    const next = reorderMediaIds(current, fromIndex, toIndex);
    if (hitRect) {
      lastHitRectRef.current = hitRect;
      lastHitIdRef.current = null;
    } else {
      lastHitRectRef.current = null;
      lastHitIdRef.current = toId;
    }
    onReorderRef.current?.(next, { fromId, toId });
  };

  const onHandlePointerDown = (event, imageId) => {
    if (!enabled) return;
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    dragIdRef.current = imageId;
    lastHitRectRef.current = null;
    lastHitIdRef.current = null;
    lastNativeEventRef.current = null;
    setDraggingId(imageId);
    detachDragListeners();
    const onMove = (moveEvent) => {
      if (lastNativeEventRef.current === moveEvent) return;
      lastNativeEventRef.current = moveEvent;
      reorderTowardPoint(moveEvent.clientX, moveEvent.clientY);
    };
    const onUp = () => {
      endDrag();
    };
    dragListenersRef.current = { onMove, onUp };
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    const target = event.currentTarget;
    if (typeof target.setPointerCapture === "function") {
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_err) {
        /* ignore */
      }
    }
  };

  const onHandlePointerUp = (event) => {
    const target = event.currentTarget;
    if (typeof target.releasePointerCapture === "function" && event.pointerId != null) {
      try {
        if (typeof target.hasPointerCapture !== "function"
          || target.hasPointerCapture(event.pointerId)) {
          target.releasePointerCapture(event.pointerId);
        }
      } catch (_err) {
        /* ignore */
      }
    }
    endDrag();
  };

  return {
    draggingId,
    onHandlePointerDown,
    onHandlePointerUp,
    endDrag,
  };
}
